import { useState, useRef, useEffect } from "react";
import { classNames } from "primereact/utils";
import styles from "./ServiceCard.module.scss";
import { IService } from "../../../models/IService";
import { PriceDisplay } from "../../../../../../common/components/PriceDisplay";

interface IServiceCardProps {
  service: IService;
  isSelected: boolean;
  onClick: () => void;
  animationDelay?: number;
  className?: string;
  isGroupClassMode?: boolean;
}

export const ServiceCard = ({
  service,
  isSelected,
  onClick,
  animationDelay = 0,
  className,
  isGroupClassMode = false,
}: IServiceCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  const formatDuration = (duration: number): string => {
    if (duration < 60) {
      return `${duration} мин.`;
    }
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    if (minutes === 0) {
      return `${hours} ч.`;
    }
    return `${hours} ч. ${minutes} мин.`;
  };

  useEffect(() => {
    if (descriptionRef.current) {
      setIsOverflowing(
        descriptionRef.current.scrollHeight > descriptionRef.current.clientHeight + 2
      );
    }
  }, [service.description]);

  const trimHtml = (html: string): string =>
    html
      .replace(/^(\s*(<br\s*\/?>|<p[^>]*>\s*(&nbsp;|\s)*<\/p>)\s*)+/gi, "")
      .replace(/(\s*(<br\s*\/?>|<p[^>]*>\s*(&nbsp;|\s)*<\/p>)\s*)+$/gi, "")
      .trim();

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  return (
    <div
      className={classNames(
        styles.card,
        {
          [styles.selected]: isSelected,
        },
        className
      )}
      onClick={onClick}
      style={{ animationDelay: `${animationDelay}ms` }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className={styles.cardContent}>
        <div className={styles.serviceInfo}>
          <h3 className={styles.serviceName}>{service.name}</h3>

          {!isGroupClassMode && (
            <div className={styles.serviceDetails}>
              <div className={styles.detailRow}>
                <div className={styles.detailItem}>
                  <span className={styles.detailIcon}>🕐</span>
                  <span className={styles.detailText}>
                    {formatDuration(service.duration)}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailIcon}>💰</span>
                  <span className={styles.detailText}>
                    <PriceDisplay minPrice={service.minPrice} maxPrice={service.maxPrice} />
                  </span>
                </div>
                {service.isRemote && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailIcon}>💻</span>
                    <span className={styles.detailText}>
                      Дистанционно (Google Meet)
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {service.description && trimHtml(service.description) && (
            <div className={styles.descriptionWrapper}>
              <div
                ref={descriptionRef}
                className={classNames(styles.serviceDescription, {
                  [styles.serviceDescriptionExpanded]: isExpanded,
                })}
                dangerouslySetInnerHTML={{ __html: trimHtml(service.description) }}
              />
              {isOverflowing && (
                <button
                  className={styles.expandButton}
                  onClick={handleExpandClick}
                  type="button"
                >
                  <span
                    className={classNames(styles.expandArrow, {
                      [styles.expandArrowUp]: isExpanded,
                    })}
                  >
                    ↓
                  </span>
                </button>
              )}
            </div>
          )}
        </div>

        <div className={styles.selectionIndicator}>
          {isSelected && (
            <div className={styles.checkmark}>
              <span className={styles.checkmarkIcon}>✓</span>
            </div>
          )}
          <div className={styles.arrow}>→</div>
        </div>
      </div>

      {/* Selection overlay */}
      <div className={styles.selectionOverlay}></div>
    </div>
  );
};
